import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Heading,
  Inline,
  Stack,
  Strong,
  Text,
  TextDropdown,
} from 'braid-design-system';
import { useState } from 'react';

import * as styles from './HealthColumn.css';
import { HealthColumn, HealthColumnPlaceholder } from './HealthColumn.tsx';

import { useApi } from '#src/api/index.ts';

const TIME_OPTIONS = {
  '24 hours': 1,
  '3 days': 3,
  '7 days': 7,
  '30 days': 30,
} as const;

type TimePeriodOption = keyof typeof TIME_OPTIONS;

const timePeriodOptions = Object.keys(TIME_OPTIONS) as TimePeriodOption[];

export const IntegrationHealthSummarySection = () => {
  const api = useApi();

  const [timePeriod, setTimePeriod] = useState<TimePeriodOption>('24 hours');

  const summaryStatistics = useQuery({
    queryKey: ['integrationSummaryStatistics', timePeriod, api],
    queryFn: ({ signal }) =>
      api.integrationSummaryStatistics.get(TIME_OPTIONS[timePeriod], signal),
  });

  if (summaryStatistics.status === 'error') {
    return (
      <Alert tone="critical">
        <Text>Failed to load integration health summary</Text>
      </Alert>
    );
  }

  return (
    <Stack space="large">
      <Inline space="none" alignY="bottom">
        <Heading level="4">Integration health</Heading>
        <Text>&nbsp;over&nbsp;</Text>
        <Text>
          <Strong>
            <TextDropdown
              id="requestTimePeriodDropdown"
              value={timePeriod}
              onChange={setTimePeriod}
              options={timePeriodOptions}
              label="Time period"
            />
          </Strong>
        </Text>
      </Inline>
      <Box className={styles.healthColumns}>
        {summaryStatistics.status === 'pending' ? (
          <>
            <HealthColumnPlaceholder
              description="Client errors"
              timePeriod={timePeriod}
              type="clientErrors"
            />
            <HealthColumnPlaceholder
              description="Client error rate"
              timePeriod={timePeriod}
              type="clientErrorRate"
            />
            <HealthColumnPlaceholder
              description="Server errors"
              timePeriod={timePeriod}
              type="serverErrors"
            />
            <HealthColumnPlaceholder
              description="Total requests"
              timePeriod={timePeriod}
              type="totalRequests"
            />
          </>
        ) : (
          <>
            <HealthColumn
              description={`Client error${summaryStatistics.data.current.clientErrors === 1 ? '' : 's'}`}
              priorValue={summaryStatistics.data.prior.clientErrors}
              value={summaryStatistics.data.current.clientErrors}
              timePeriod={timePeriod}
              type="clientErrors"
            />
            <HealthColumn
              description="Client error rate"
              value={
                summaryStatistics.data.current.totalRequests === 0
                  ? 0
                  : (summaryStatistics.data.current.clientErrors /
                      summaryStatistics.data.current.totalRequests) *
                    100
              }
              priorValue={
                summaryStatistics.data.prior.totalRequests === 0
                  ? 0
                  : (summaryStatistics.data.prior.clientErrors /
                      summaryStatistics.data.prior.totalRequests) *
                    100
              }
              timePeriod={timePeriod}
              type="clientErrorRate"
            />
            <HealthColumn
              description={`Server error${summaryStatistics.data.current.serverErrors === 1 ? '' : 's'}`}
              priorValue={summaryStatistics.data.prior.serverErrors}
              value={summaryStatistics.data.current.serverErrors}
              timePeriod={timePeriod}
              type="serverErrors"
            />
            <HealthColumn
              description={`Total request${summaryStatistics.data.current.totalRequests === 1 ? '' : 's'}`}
              value={summaryStatistics.data.current.totalRequests}
              priorValue={summaryStatistics.data.prior.totalRequests}
              timePeriod={timePeriod}
              type="totalRequests"
            />
          </>
        )}
      </Box>
    </Stack>
  );
};
